import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  IconButton,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Block as BlockIcon,

} from '@mui/icons-material';
import axios from 'axios';

const PromotionalTiers = () => {
  const [codes, setCodes] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Form state
  const [formData, setFormData] = useState({
    discountType: 'FREE_TIER_UPGRADE',
    tier: 'PROFESSIONAL',
    durationDays: 30,
    subscriptionDuration: 'MONTHLY',
    discountPercentage: 50,
    discountAmount: 10,
    maxRedemptions: 1,
    validUntil: '',
    campaignName: '',
    notes: ''
  });

  const fetchCodes = useCallback(async () => {
    try {
      const response = await axios.get('/api/promotional-tiers/codes');
      if (response.data.success) {
        setCodes(response.data.codes);
      }
    } catch (error) {
      console.error('Error fetching codes:', error);
      showSnackbar('Failed to fetch promotional codes', 'error');
    }
  }, []);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);



  const handleGenerateCode = async () => {
    setLoading(true);
    try {
      const response = await axios.post('/api/promotional-tiers/generate', formData);
      if (response.data.success) {
        showSnackbar(`Code generated: ${response.data.code.code}`, 'success');
        setOpenDialog(false);
        fetchCodes();
        resetForm();
      }
    } catch (error) {
      console.error('Error generating code:', error);
      showSnackbar(error.response?.data?.error || 'Failed to generate code', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeCode = async (codeId) => {
    if (!window.confirm('Are you sure you want to revoke this promotional code?')) {
      return;
    }

    try {
      const response = await axios.post(`/api/promotional-tiers/revoke/${codeId}`);
      if (response.data.success) {
        showSnackbar('Code revoked successfully', 'success');
        fetchCodes();
      }
    } catch (error) {
      console.error('Error revoking code:', error);
      showSnackbar('Failed to revoke code', 'error');
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    showSnackbar('Code copied to clipboard', 'success');
  };

  const resetForm = () => {
    setFormData({
      discountType: 'FREE_TIER_UPGRADE',
      tier: 'PROFESSIONAL',
      durationDays: 30,
      subscriptionDuration: 'MONTHLY',
      discountPercentage: 50,
      discountAmount: 10,
      maxRedemptions: 1,
      validUntil: '',
      campaignName: '',
      notes: ''
    });
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'REDEEMED': return 'info';
      case 'EXPIRED': return 'warning';
      case 'REVOKED': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        {/* Header Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
          >
            Generate New Code
          </Button>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Codes
                </Typography>
                <Typography variant="h4">
                  {codes.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Active Codes
                </Typography>
                <Typography variant="h4">
                  {codes.filter(c => c.status === 'ACTIVE').length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Redeemed
                </Typography>
                <Typography variant="h4">
                  {codes.filter(c => c.status === 'REDEEMED').length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Redemptions
                </Typography>
                <Typography variant="h4">
                  {codes.reduce((sum, c) => sum + (c.redemptions?.length || 0), 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Codes Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Tier</TableCell>
                <TableCell>Discount</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Max Uses</TableCell>
                <TableCell>Redemptions</TableCell>
                <TableCell>Campaign</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {codes.map((code) => (
                <TableRow key={code.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {code.code}
                      </Typography>
                      <IconButton size="small" onClick={() => handleCopyCode(code.code)}>
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={code.discountType === 'FREE_TIER_UPGRADE' ? 'Free Upgrade' :
                             code.discountType === 'PERCENTAGE_DISCOUNT' ? 'Percentage' : 'Fixed Amount'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={code.grantedTier}
                      color={code.grantedTier === 'ENTERPRISE' ? 'primary' : 'secondary'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {code.discountType === 'FREE_TIER_UPGRADE' ? 'Free' :
                     code.discountType === 'PERCENTAGE_DISCOUNT' ? `${code.discountPercentage}%` :
                     `$${code.discountAmount}`}
                  </TableCell>
                  <TableCell>
                    {code.discountType === 'FREE_TIER_UPGRADE' ? `${code.durationDays} days` :
                     code.subscriptionDuration || '-'}
                  </TableCell>
                  <TableCell>{code.maxRedemptions === 0 ? 'Unlimited' : code.maxRedemptions}</TableCell>
                  <TableCell>{code.currentRedemptions || 0}</TableCell>
                  <TableCell>{code.campaignName || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={code.status}
                      color={getStatusColor(code.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(code.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {code.status === 'ACTIVE' && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRevokeCode(code.id)}
                      >
                        <BlockIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {codes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      No promotional codes generated yet
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Generate Code Dialog */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Generate Promotional Code</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Discount Type */}
              <FormControl fullWidth>
                <InputLabel>Discount Type</InputLabel>
                <Select
                  value={formData.discountType}
                  label="Discount Type"
                  onChange={(e) => setFormData({ ...formData, discountType: e.target.value })}
                >
                  <MenuItem value="FREE_TIER_UPGRADE">Free Tier Upgrade</MenuItem>
                  <MenuItem value="PERCENTAGE_DISCOUNT">Percentage Discount</MenuItem>
                  <MenuItem value="FIXED_AMOUNT_DISCOUNT">Fixed Amount Discount</MenuItem>
                </Select>
              </FormControl>

              {/* Tier */}
              <FormControl fullWidth>
                <InputLabel>Tier</InputLabel>
                <Select
                  value={formData.tier}
                  label="Tier"
                  onChange={(e) => setFormData({ ...formData, tier: e.target.value })}
                >
                  <MenuItem value="PROFESSIONAL">Professional</MenuItem>
                  <MenuItem value="ENTERPRISE">Enterprise</MenuItem>
                </Select>
              </FormControl>

              {/* Conditional Fields Based on Discount Type */}
              {formData.discountType === 'FREE_TIER_UPGRADE' ? (
                <TextField
                  fullWidth
                  label="Duration (days)"
                  type="number"
                  value={formData.durationDays}
                  onChange={(e) => setFormData({ ...formData, durationDays: parseInt(e.target.value) })}
                  inputProps={{ min: 1 }}
                  helperText="How many days the free tier upgrade lasts"
                />
              ) : (
                <>
                  <FormControl fullWidth>
                    <InputLabel>Subscription Duration</InputLabel>
                    <Select
                      value={formData.subscriptionDuration}
                      label="Subscription Duration"
                      onChange={(e) => setFormData({ ...formData, subscriptionDuration: e.target.value })}
                    >
                      <MenuItem value="MONTHLY">Monthly</MenuItem>
                      <MenuItem value="ANNUAL">Annual</MenuItem>
                      <MenuItem value="LIFETIME">Lifetime</MenuItem>
                    </Select>
                  </FormControl>

                  {formData.discountType === 'PERCENTAGE_DISCOUNT' && (
                    <TextField
                      fullWidth
                      label="Discount Percentage"
                      type="number"
                      value={formData.discountPercentage}
                      onChange={(e) => setFormData({ ...formData, discountPercentage: parseFloat(e.target.value) })}
                      inputProps={{ min: 0, max: 100, step: 0.1 }}
                      helperText="Percentage off (e.g., 50 for 50% off)"
                    />
                  )}

                  {formData.discountType === 'FIXED_AMOUNT_DISCOUNT' && (
                    <TextField
                      fullWidth
                      label="Discount Amount ($)"
                      type="number"
                      value={formData.discountAmount}
                      onChange={(e) => setFormData({ ...formData, discountAmount: parseFloat(e.target.value) })}
                      inputProps={{ min: 0, step: 0.01 }}
                      helperText="Fixed dollar amount off (e.g., 10 for $10 off)"
                    />
                  )}
                </>
              )}

              {/* Max Redemptions */}
              <TextField
                fullWidth
                label="Max Redemptions"
                type="number"
                value={formData.maxRedemptions}
                onChange={(e) => setFormData({ ...formData, maxRedemptions: parseInt(e.target.value) })}
                inputProps={{ min: 0 }}
                helperText="0 = unlimited, 1 = single use, >1 = multi-use"
              />

              {/* Valid Until */}
              <TextField
                fullWidth
                label="Valid Until (optional)"
                type="datetime-local"
                value={formData.validUntil}
                onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText="Leave empty for no expiration date"
              />

              {/* Campaign Name */}
              <TextField
                fullWidth
                label="Campaign Name (optional)"
                value={formData.campaignName}
                onChange={(e) => setFormData({ ...formData, campaignName: e.target.value })}
                helperText="e.g., 'Black Friday 2024', 'New Year Sale'"
              />

              {/* Notes */}
              <TextField
                fullWidth
                label="Notes (optional)"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                helperText="Internal notes about this promotional code"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button
              onClick={handleGenerateCode}
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Generating...' : 'Generate Code'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
    </Box>
  );
};

export default PromotionalTiers;

